import { useEntity } from '@/hooks/queries/useEntityQuery';
import { fixtureServiceAdapter } from '@/services/adapters/fixtureServiceAdapter';
import { LightingFixture } from '@/types/floorPlanViewer';
import { useAppStore } from '@/stores/appStore';
import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryKeys';

export function useFixturesStandardized(floorPlanId: string) {
  const {
    data,
    isLoading,
    error,
    refetch,
    create,
    update,
    delete: deleteFixture,
    ...mutations
  } = useEntity<LightingFixture>('lighting_fixtures', floorPlanId, fixtureServiceAdapter);

  const queryClient = useQueryClient();

  // Deleting a fixture can also delete its now-empty circuit (see fixtureServiceAdapter),
  // so the circuits cache must be refreshed for the Loads tab to stay in sync
  const deleteFixtureWithCircuitSync = async (
    id: string,
    options?: { showSuccessToast?: boolean },
  ) => {
    const result = await deleteFixture(id, options);
    queryClient.invalidateQueries({
      queryKey: queryKeys.entitiesByFloorPlan('circuits', floorPlanId),
    });
    return result;
  };

  // Fix: Use separate selectors to avoid infinite loops
  const viewMode = useAppStore(state => state.ui.viewMode);
  const showOnlyUnassigned = false; // TODO: Add this to store if needed

  // Filter fixtures based on UI state
  const visibleFixtures = useMemo(() => {
    if (!data) return [];

    let filtered = [...data];

    if (showOnlyUnassigned) {
      filtered = filtered.filter(f => !f.circuit_id);
    }

    return filtered;
  }, [data, showOnlyUnassigned]);

  // Calculate statistics
  const statistics = useMemo(() => {
    if (!data) return { totalWattage: 0, typeStats: {}, circuitStats: {} };

    const totalWattage = data.reduce((sum, fixture) => sum + (fixture.wattage || 0), 0);

    const typeStats = data.reduce(
      (acc, fixture) => {
        acc[fixture.fixture_type] = (acc[fixture.fixture_type] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    const circuitStats = data.reduce(
      (acc, fixture) => {
        const circuitId = fixture.circuit_id || 'unassigned';
        acc[circuitId] = (acc[circuitId] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    return { totalWattage, typeStats, circuitStats };
  }, [data]);

  // Enhanced create function with floor plan assignment
  const createFixture = async (
    fixtureData: Partial<LightingFixture>,
    options?: { showSuccessToast?: boolean },
  ) => {
    const fixtureWithFloorPlan = {
      ...fixtureData,
      floor_plan_id: floorPlanId,
    };

    const result = options
      ? await create(fixtureWithFloorPlan, options)
      : await create(fixtureWithFloorPlan);

    return result;
  };

  // Enhanced update function
  const updateFixture = async (
    id: string,
    updates: Partial<LightingFixture>,
    options?: { showSuccessToast?: boolean },
  ) => {
    return update({ id, updates, showSuccessToast: options?.showSuccessToast });
  };

  // Bulk operations
  const assignToCircuit = async (fixtureIds: string[], circuitId: string | null) => {
    const promises = fixtureIds.map(id => updateFixture(id, { circuit_id: circuitId }));
    return Promise.all(promises);
  };

  const updateWattage = async (fixtureIds: string[], wattage: number) => {
    const promises = fixtureIds.map(id => updateFixture(id, { wattage }));
    return Promise.all(promises);
  };

  return {
    // Data
    fixtures: data,
    visibleFixtures,

    // Loading states
    isLoading,
    error,

    // Mutation states
    isCreating: mutations.isCreating,
    isUpdating: mutations.isUpdating,
    isDeleting: mutations.isDeleting,

    // Operations
    createFixture,
    updateFixture,
    deleteFixture: deleteFixtureWithCircuitSync,
    refetch,

    // Bulk operations
    assignToCircuit,
    updateWattage,

    // Statistics
    totalWattage: statistics.totalWattage,
    getFixtureTypeStats: () => statistics.typeStats,
    getCircuitStats: () => statistics.circuitStats,
  };
}
